'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  position: string;
  company: string;
  content: string;
  rating: number;
  image: string;
  alt: string;
  videoUrl?: string;
}

interface TestimonialsSectionProps {
  className?: string;
}

const TestimonialsSection = ({ className = '' }: TestimonialsSectionProps) => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Michael Chen',
    position: 'Operations Director',
    company: 'Global Mining Corp',
    content: 'MylesDrive has been instrumental in our African operations. Their understanding of mining logistics, combined with real-time tracking and exceptional safety standards, has made them our exclusive logistics partner across six countries. The professionalism and reliability are unmatched.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5d2ed23-1766852097644.png",
    alt: 'Professional Asian mining executive in hard hat and safety vest at mining site',
    videoUrl: '#'
  },
  {
    id: 2,
    name: 'Sarah Omondi',
    position: 'Project Manager',
    company: 'East Africa Infrastructure Ltd',
    content: 'Managing heavy equipment logistics for our EPC projects across East Africa requires a partner who understands both the technical challenges and regional complexities. MylesDrive delivers on both fronts consistently. Their route surveys and customs expertise have saved us countless delays.',
    rating: 5,
    image: "https://images.unsplash.com/photo-1575282366139-d605e098a825",
    alt: 'Professional African woman project manager with blueprints at construction site',
    videoUrl: '#'
  },
  {
    id: 3,
    name: 'Robert Mwamba',
    position: 'Supply Chain Director',
    company: 'Continental Resources Group',
    content: 'The technology platform MylesDrive provides gives us complete visibility into our shipments. Real-time tracking, digital documentation, and proactive communication have transformed how we manage cross-border logistics. Their team truly understands the African market.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19ca44cd8-1767280275062.png",
    alt: 'Professional African supply chain director reviewing logistics data on tablet',
    videoUrl: '#'
  },
  {
    id: 4,
    name: 'Emma Larsson',
    position: 'Logistics Coordinator',
    company: 'Nordic Mining Solutions',
    content: 'As a European company operating in Africa, we needed a logistics partner who could bridge both worlds. MylesDrive\'s international standards combined with deep local knowledge have been invaluable. Their safety record and environmental commitment align perfectly with our values.',
    rating: 5,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_113881709-1763300800694.png",
    alt: 'Professional European woman logistics coordinator with safety equipment in warehouse',
    videoUrl: '#'
  }];


  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-secondary" />
            <span className="font-body text-sm font-semibold text-secondary">Client Testimonials</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            Hear from mining executives and project managers who rely on MylesDrive for their critical logistics operations across Africa.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-card rounded-2xl shadow-elevation-4 overflow-hidden">
            <div className="grid lg:grid-cols-5 gap-0">
              {/* Image Side */}
              <div className="lg:col-span-2 relative h-96 lg:h-auto overflow-hidden">
                <AppImage
                  src={testimonials[activeTestimonial].image}
                  alt={testimonials[activeTestimonial].alt}
                  className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-card to-transparent" />
                
                {/* Video Play Button */}
                {testimonials[activeTestimonial].videoUrl &&
                <button className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-accent/90 backdrop-blur-sm p-6 rounded-full hover:bg-accent transition-all duration-300 hover:scale-110 shadow-golden-lg">
                    <Icon name="PlayIcon" size={32} className="text-primary ml-1" />
                  </button>
                }
              </div>

              {/* Content Side */}
              <div className="lg:col-span-3 p-8 lg:p-12">
                {/* Quote Icon */}
                <div className="mb-6">
                  <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-accent" />
                </div>

                {/* Rating */}
                <div className="flex items-center space-x-1 mb-6">
                  {[...Array(testimonials[activeTestimonial].rating)].map((_, index) =>
                  <Icon key={index} name="StarIcon" size={20} className="text-accent" variant="solid" />
                  )}
                </div>

                {/* Content */}
                <blockquote className="font-body text-lg text-foreground leading-relaxed mb-8">
                  "{testimonials[activeTestimonial].content}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-headline font-bold text-xl text-foreground mb-1">
                      {testimonials[activeTestimonial].name}
                    </h4>
                    <p className="font-body text-sm text-secondary mb-1">
                      {testimonials[activeTestimonial].position}
                    </p>
                    <p className="font-body text-sm text-muted-foreground">
                      {testimonials[activeTestimonial].company}
                    </p>
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={prevTestimonial}
                      className="p-3 bg-muted hover:bg-accent/10 rounded-lg transition-colors duration-300 group"
                      aria-label="Previous testimonial">

                      <Icon name="ChevronLeftIcon" size={20} className="text-muted-foreground group-hover:text-secondary" />
                    </button>
                    <button
                      onClick={nextTestimonial}
                      className="p-3 bg-muted hover:bg-accent/10 rounded-lg transition-colors duration-300 group"
                      aria-label="Next testimonial">

                      <Icon name="ChevronRightIcon" size={20} className="text-muted-foreground group-hover:text-secondary" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Testimonial Indicators */}
          <div className="flex items-center justify-center space-x-2 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
              index === activeTestimonial ? 'w-8 bg-accent' : 'w-2 bg-muted-foreground/30'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        {/* All Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {testimonials.map((testimonial, index) =>
          <button
            key={testimonial.id}
            onClick={() => setActiveTestimonial(index)}
            className={`bg-card rounded-xl p-6 text-left transition-all duration-300 ${
            index === activeTestimonial ?
            'shadow-elevation-3 border-2 border-accent' :
            'shadow-elevation-1 hover:shadow-elevation-2 border border-border'}`
            }>

              <div className="flex items-center space-x-3 mb-4">
                <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                  src={testimonial.image}
                  alt={testimonial.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-headline font-bold text-sm text-foreground truncate">{testimonial.name}</h5>
                  <p className="font-body text-xs text-muted-foreground truncate">{testimonial.company}</p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {[...Array(testimonial.rating)].map((_, i) =>
              <Icon key={i} name="StarIcon" size={14} className="text-accent" variant="solid" />
              )}
              </div>
            </button>
          )}
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;